import { useState, useCallback, useEffect } from 'react';
import { showToast } from '../components/atoms/Toast/Toast';
import authService from '../services/authService';
 
const formatDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
 
export const useAuthLogs = ({ isAuthenticated, accessToken, activeSection, fetchLiveLogs }) => {
 
  const [logsData,            setLogsData]            = useState(null);
  const [loadingLogs,         setLoadingLogs]         = useState(false);
  const [startDate,           setStartDate]           = useState(null);
  const [endDate,             setEndDate]             = useState(null);
  const [authSelectedAdminId, setAuthSelectedAdminId] = useState(null);
  const [selectedUserId,      setSelectedUserId]      = useState(null);
  const [userDetailData,      setUserDetailData]      = useState(null);
  const [loadingUserDetail,   setLoadingUserDetail]   = useState(false);
  const [refreshingAuth,      setRefreshingAuth]      = useState(false);
  const [isInternal,          setIsInternal]          = useState(null);
 
  const fetchLogs = useCallback(async (start, end, adminId) => {
    if (!isAuthenticated || !accessToken) return;
    setLoadingLogs(true);
    try {
      const data = await authService.getGlobalOverview(
        'auth',
        formatDate(start ?? startDate),
        formatDate(end   ?? endDate),
        adminId ?? authSelectedAdminId,
        isInternal
      );
      setLogsData(data);
    } catch (e) {
      showToast(e.message || 'Erreur chargement logs', 'error');
    } finally {
      setLoadingLogs(false);
    }
  }, [isAuthenticated, accessToken, startDate, endDate, authSelectedAdminId, isInternal]);
 
  const fetchUserDetail = useCallback(async (userId) => {
    if (!userId) { setUserDetailData(null); return; }
    setLoadingUserDetail(true);
    try {
      const data = await authService.getUserDetailed(
        userId, 'auth',
        formatDate(startDate),
        formatDate(endDate),
        authSelectedAdminId,
        isInternal
      );
      setUserDetailData(data);
      console.log(
        'recent_activities:', data?.recent_activities?.length,
        '| total by_functionality:', (data?.by_functionality || []).reduce((s, f) => s + (f.total_usage || 0), 0),
        '| exemple:', data?.recent_activities?.[0],
      );
    } catch (e) {
      showToast(e.message || 'Erreur détails utilisateur', 'error');
      setUserDetailData(null);
    } finally {
      setLoadingUserDetail(false);
    }
  }, [startDate, endDate, authSelectedAdminId, isInternal]);
 
  useEffect(() => {
    if (activeSection !== 'userLogs') return;
    fetchLogs(startDate, endDate, authSelectedAdminId);
    fetchLiveLogs('auth', authSelectedAdminId);
  }, [activeSection, startDate, endDate, authSelectedAdminId, isInternal]); // eslint-disable-line
 
  useEffect(() => {
    if (selectedUserId) fetchUserDetail(selectedUserId);
  }, [startDate, endDate, authSelectedAdminId, isInternal]); // eslint-disable-line
 
  const handleUserSelect = useCallback((userId) => {
    setSelectedUserId(userId);
    fetchUserDetail(userId);
  }, [fetchUserDetail]);
 
  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };
 
  const handleAuthAdminSelect = useCallback((adminId) => {
    setAuthSelectedAdminId(adminId ?? null);
    setUserDetailData(null);
    setSelectedUserId(null);
  }, []);
 
  const handleIsInternalChange = useCallback((val) => {
    setIsInternal(val);
    setUserDetailData(null);
    setSelectedUserId(null);
  }, []);
 
  const handleRefresh = useCallback(async () => {
    if (refreshingAuth) return;
    setRefreshingAuth(true);
    try {
      await fetchLogs(startDate, endDate, authSelectedAdminId);
      await fetchLiveLogs('auth', authSelectedAdminId);
      if (selectedUserId) await fetchUserDetail(selectedUserId);
    } catch {
      showToast('Erreur rafraîchissement', 'error');
    } finally {
      setRefreshingAuth(false);
    }
  }, [refreshingAuth, fetchLogs, fetchLiveLogs, startDate, endDate,
      authSelectedAdminId, selectedUserId, fetchUserDetail]);
 
  const patchActivityCount = useCallback((data) => {
    setLogsData((prev) => {
      if (!prev?.all_users) return prev;
      return {
        ...prev,
        all_users: prev.all_users.map((u) =>
          String(u.user_id) === String(data.user_id)
            ? { ...u, activity_count: (u.activity_count || 0) + 1 }
            : u
        ),
      };
    });
  }, []);
 
  const patchUserDetail = useCallback((data) => {
    setUserDetailData((prev) =>
      prev && String(prev.user_id) === String(data.user_id)
        ? { ...prev, total_activities: data.alltime_total }
        : prev
    );
  }, []);
 
  const invalidateDetail = useCallback((userId) => {
    setUserDetailData((prev) =>
      prev && String(prev.user_id) === String(userId) ? null : prev
    );
  }, []);
 
  const reset = useCallback(() => {
    setLogsData(null);
    setStartDate(null);
    setEndDate(null);
    setSelectedUserId(null);
    setUserDetailData(null);
    setAuthSelectedAdminId(null);
    setIsInternal(null);
  }, []);
 
  return {
    patchActivityCount,
    patchUserDetail,
    invalidateDetail,
    reset,
    props: {
      logsData,
      loadingLogs,
      startDate,
      endDate,
      onDateChange:        handleDateChange,
      selectedUserId,
      onUserSelect:        handleUserSelect,
      userDetailData,
      loadingUserDetail,
      authSelectedAdminId,
      onAuthAdminSelect:   handleAuthAdminSelect,
      onRefreshAuthLogs:   handleRefresh,
      refreshingAuth,
      authIsInternal:         isInternal,          
      onAuthIsInternalChange: handleIsInternalChange
    },
  };
};